'use client';

import { useMemo } from 'react';
import DeliveryTruckIcon from '@/components/icons/DeliveryTruckIcon';
import InfoIcon from '@/components/icons/InfoIcon';
import LicenseIcon from '@/components/icons/LicenseIcon';
import MailIcon from '@/components/icons/MailIcon';
import MarkerPinIcon from '@/components/icons/MarkerPinIcon';
import PhoneIcon from '@/components/icons/PhoneIcon';
import RouteIcon from '@/components/icons/RouteIcon';
import StorefrontIcon from '@/components/icons/StorefrontIcon';
import XCircleIcon from '@/components/icons/XCircleIcon';
import { Button } from '@/components';
import { formatDailyHours } from '@/lib/store-hours';
import { openDirections } from '@/lib/directions-link';
import { setStoredStoreId } from '@/lib/store-storage';
import { useRouter } from 'next/navigation';

type Props = {
  storeName: string;
  city?: string | null;
  address?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  email?: string | null;
  openTime?: string | null;
  closeTime?: string | null;
  deliveryOpenTime?: string | null;
  deliveryCloseTime?: string | null;
  holidayOpenTime?: string | null;
  holidayCloseTime?: string | null;
  licenseNumber?: string | null;
  covaLocationId?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  googlePlaceId?: string | null;
};

function InfoRow({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <div className="flex items-center gap-2 text-body-16-desktop font-medium text-blue-100">
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-blue-80">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

export default function LocationHoursInfoSection({
  storeName,
  city,
  address,
  postalCode,
  phone,
  email,
  openTime,
  closeTime,
  deliveryOpenTime,
  deliveryCloseTime,
  holidayOpenTime,
  holidayCloseTime,
  licenseNumber,
  covaLocationId,
  latitude,
  longitude,
  googlePlaceId,
}: Props) {
  const router = useRouter();

  /**
   * Pickup and Delivery make this the active store first, otherwise the shopper
   * lands on a product grid still scoped to whichever store they had before.
   */
  const shopHere = (fulfillment: 'pickup' | 'delivery') => {
    if (covaLocationId) setStoredStoreId(covaLocationId);
    router.push(`/search?fulfillment=${fulfillment}`);
  };
  const displayAddress = useMemo(() => {
    const parts = [address?.trim(), city?.trim(), postalCode?.trim()].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : `${storeName}, Calgary AB`;
  }, [address, city, postalCode, storeName]);
  const mapEmbedUrl = useMemo(
    () => `https://www.google.com/maps?q=${encodeURIComponent(displayAddress)}&z=15&output=embed`,
    [displayAddress]
  );

  return (
    <section className="relative mt-4 sm:mt-6" aria-labelledby="location-hours-info-heading">
      <div className="mx-auto w-full">
        <div className="rounded-2xl">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 id="location-hours-info-heading" className="text-h3-mobile font-bold text-blue-100 sm:text-h3-desktop">
                Hours & Info
              </h2>
              <p className="mt-2 max-w-[48ch] text-body-16-desktop text-blue-100/80">
                We are here for you 7 days a week. Shop in-store until late, or order online for fast local delivery and
                convenient pickup.
              </p>

              <div className="mt-4 grid grid-cols-3 gap-2">
                <Button
                  variant="bordered"
                  size="lg"
                  startIcon={<StorefrontIcon size={16} color="currentColor" />}
                  className="rounded-xl border border-neutral-10 bg-constant-white text-body-16-desktop font-semibold text-blue-80"
                  onClick={() => shopHere('pickup')}
                >
                  Pickup
                </Button>
                <Button
                  variant="bordered"
                  size="lg"
                  startIcon={<DeliveryTruckIcon size={16} color="currentColor" />}
                  className="rounded-xl border border-neutral-10 bg-constant-white text-body-16-desktop font-semibold text-blue-80"
                  onClick={() => shopHere('delivery')}
                >
                  Delivery
                </Button>
                <Button
                  variant="bordered"
                  size="lg"
                  startIcon={<RouteIcon size={16} color="currentColor" />}
                  className="rounded-xl border border-neutral-10 bg-constant-white text-body-16-desktop font-semibold text-blue-80"
                  onClick={() =>
                    openDirections({
                      name: storeName,
                      address,
                      city,
                      postalCode,
                      latitude,
                      longitude,
                      googlePlaceId,
                    })
                  }
                >
                  Route
                </Button>
              </div>

              <div className="mt-5 space-y-3">
                <InfoRow icon={<MarkerPinIcon size={16} color="currentColor" />} text={displayAddress} />
                <InfoRow icon={<PhoneIcon size={16} color="currentColor" />} text={phone?.trim() || '403-444-0977'} />
                <InfoRow icon={<MailIcon size={16} color="currentColor" />} text={email?.trim() || 'coachhill@dank.ca'} />
                {licenseNumber?.trim() && (
                  <InfoRow
                    icon={<LicenseIcon size={16} color="currentColor" />}
                    text={`License: ${licenseNumber.trim()}`}
                  />
                )}
              </div>

              <div className="my-5 h-px w-full bg-neutral-15" />

              <h3 className="text-h4-mobile font-bold text-blue-100 sm:text-h4-desktop">How to enter Dank dispensaries</h3>
              <p className="mt-2 text-body-16-desktop font-medium text-blue-100/80">
                For security, our front door stays locked. But getting in is easy:
              </p>
              <ol className="mt-1 space-y-1 text-body-16-desktop text-blue-100">
                <li className="font-semibold">1. Find the keypad/intercom by the door</li>
                <li>2. Press the round button at the bottom, just once.</li>
                <li>3. Our budtenders will buzz you in right away.</li>
              </ol>

              <div className="mt-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <XCircleIcon size={18} color="#E53935" className="mt-0.5 shrink-0" />
                    <p className="text-body-14-desktop text-blue-100">
                      <span className="font-semibold">No need to hold the button.</span> A quick press works best.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <InfoIcon size={18} color="#FFD52F" className="mt-0.5 shrink-0" />
                    <p className="text-body-14-desktop text-blue-100">
                      <span className="font-semibold">Need help?</span> Just wave, our team is watching and happy to help.
                    </p>
                  </div>
              </div>

              <Button
                variant="primary"
                size="lg"
                className="mt-5 w-full rounded-xl"
                onClick={() => shopHere('pickup')}
              >
                Shop this store
              </Button>
            </div>

            <div className="flex h-full flex-col lg:col-span-7">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl border border-neutral-10 bg-constant-white px-3 py-2.5 text-center">
                  <div className="text-body-16-desktop font-bold text-blue-100">Store hours</div>
                  <div className="text-body-14-desktop font-semibold text-blue-100/75">{formatDailyHours(openTime, closeTime)}</div>
                </div>
                <div className="rounded-xl border border-neutral-10 bg-constant-white px-3 py-2.5 text-center">
                  <div className="text-body-16-desktop font-bold text-blue-100">Delivery hours</div>
                  <div className="text-body-14-desktop font-semibold text-blue-100/75">{formatDailyHours(deliveryOpenTime, deliveryCloseTime)}</div>
                </div>
                <div className="rounded-xl border border-neutral-10 bg-constant-white px-3 py-2.5 text-center">
                  <div className="inline-flex items-center justify-center gap-1 text-body-16-desktop font-bold text-blue-100">
                    Holiday hours
                    <InfoIcon size={13} color="currentColor" />
                  </div>
                  <div className="text-body-14-desktop font-semibold text-blue-100/75">{formatDailyHours(holidayOpenTime, holidayCloseTime)}</div>
                </div>
              </div>

              <div className="relative mt-2 min-h-[260px] flex-1 overflow-hidden rounded-2xl border border-neutral-15 bg-bg-2 lg:min-h-0">
                <iframe
                  title={`${storeName} map`}
                  src={mapEmbedUrl}
                  className="absolute inset-0 h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
